// SEO-specific metadata
export interface PostSEO {
  metaTitle?: string;        // Custom title for SEO (falls back to title)
  metaDescription?: string;  // Custom description for SEO (falls back to excerpt)
  keywords?: string[];       // Additional SEO keywords
  canonical?: string;        // Custom canonical URL
  ogImage?: string;          // Custom Open Graph image
  ogImageAlt?: string;       // Alt text for OG image
  noIndex?: boolean;         // Prevent indexing
  author?: string;           // Override default author
  section?: string;          // Article section/category
  modifiedDate?: string;     // Last modified date
}

// Post metadata type (from generated index)
export interface PostMeta {
  slug: string;
  title: string;
  date: string;
  excerpt: string;
  coverImage: string | null;
  tags: string[];
  // SEO fields
  seo?: PostSEO;
  wordCount?: number;
  readingTime?: number;
}

// Full post with content component
export interface Post extends PostMeta {
  Content: React.ComponentType;
}

// Import the generated post index
import postsIndex from "@/generated/posts.json";

/**
 * Get all post metadata (for listing pages)
 */
export function getAllPosts(): PostMeta[] {
  return postsIndex as PostMeta[];
}

/**
 * Get a single post's metadata by slug
 */
export function getPostMeta(slug: string): PostMeta | undefined {
  return (postsIndex as PostMeta[]).find((post) => post.slug === slug);
}

/**
 * Dynamically import a post's MDX content
 * Returns the component and frontmatter
 */
export async function getPost(slug: string): Promise<Post | null> {
  const meta = getPostMeta(slug);
  if (!meta) return null;

  try {
    // Dynamic import of the MDX file
    const module = await import(`@content/posts/${slug}.mdx`);
    
    return {
      ...meta,
      Content: module.default,
    };
  } catch (error) {
    console.error(`Failed to load post: ${slug}`, error);
    return null;
  }
}

/**
 * Get posts by tag
 */
export function getPostsByTag(tag: string): PostMeta[] {
  return (postsIndex as PostMeta[]).filter((post) =>
    post.tags.includes(tag)
  );
}

/**
 * Get all unique tags
 */
export function getAllTags(): string[] {
  const tags = new Set<string>();
  (postsIndex as PostMeta[]).forEach((post) => {
    post.tags.forEach((tag) => tags.add(tag));
  });
  return Array.from(tags).sort();
}
